import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { RNButton } from '@/components';
import { PaymentModal, PaymentModalContent } from '@/components/residents/payments';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { formatCurrency } from '@/utils/common/formatting';
import { useAppSelector } from '@/store';
import { selectCalculatedFees } from '@/store/selectors/paymentsSelectors';
import { useGetLeaseBalanceQuery } from '@/store/api';
import { 
  getPaymentMethodLabel, 
  generatePaymentSummaryRows,
  formatConvenienceFee 
} from '@/utils/residents/paymentUtils';
import { useToast } from '@/contexts/admin/ToastContext';

type Params = {
  leaseId: number;
  amount: number;
  payFullAmount: boolean;
  paymentMethod?: any;
  onBehalfOfName?: string;
  suppressOverlay?: boolean;
};

type ConfirmInlineProps = Partial<Params> & {
  onRequestClose?: () => void;
};

export const ConfirmPaymentDetailsModal: React.FC<ConfirmInlineProps> = (props) => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const routeParams = (route.params || {}) as Params;
  
  // Get props or route params
  const leaseId = props.leaseId ?? routeParams.leaseId;
  const amount = props.amount ?? routeParams.amount;
  const payFullAmount = props.payFullAmount ?? routeParams.payFullAmount;
  const paymentMethod = props.paymentMethod ?? routeParams.paymentMethod;
  const onBehalfOfName = props.onBehalfOfName ?? routeParams.onBehalfOfName;
  const suppressOverlay = props.suppressOverlay ?? routeParams.suppressOverlay;
  
  // Get calculated fees from Redux store
  const calculatedFees = useAppSelector(selectCalculatedFees);
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  
  // Fresh balance refetch for zero-balance verification when paying full balance
  const { refetch: refetchBalance } = useGetLeaseBalanceQuery(leaseId as number, { skip: true }); // Skip to avoid Entrata errors
  const { showError } = useToast();

  // Determine if this is owner payment
  const isOwner = useMemo(() => {
    return !onBehalfOfName || onBehalfOfName.trim() === `${authUser?.firstName || ''} ${authUser?.lastName || ''}`.trim();
  }, [onBehalfOfName, authUser]);

  // Payment method display
  const methodLabel = useMemo(() => {
    return getPaymentMethodLabel(paymentMethod);
  }, [paymentMethod]);

  // Generate payment summary rows matching web implementation
  const summaryRows = useMemo(() => {
    if (!calculatedFees) return [];
    
    return generatePaymentSummaryRows(
      isOwner,
      onBehalfOfName || 'Resident',
      paymentMethod,
      Number(amount || 0),
      calculatedFees.convenienceFee,
      calculatedFees.total
    );
  }, [isOwner, onBehalfOfName, paymentMethod, amount, calculatedFees]);

  const onClose = () => {
    if (props.onRequestClose) return props.onRequestClose();
    navigation.goBack();
  };

  const onConfirm = async () => {
    // Zero-balance verification before posting when paying full balance
    if (payFullAmount) {
      try {
        const res: any = await refetchBalance();
        const remoteBalanceDue = Number(
          (res?.data?.balanceDue ?? res?.data?.response?.balanceDue ?? 0) as number,
        );
        if (!(remoteBalanceDue > 0)) {
          showError('The resident does not owe a balance to be paid.');
          return;
        }
      } catch (_err) {
        // If refetch fails, fall back to local check below
      }
    }

    // Fallback/local guard and proceed - navigate directly to processing
    if (!payFullAmount || (payFullAmount && Number(amount || 0) > 0)) {
      navigation.replace('TransactionProcessing', {
        leaseId,
        amount,
        payFullAmount,
        paymentMethodId: paymentMethod?.id,
        residentId: authUser?.id,
      });
    } else {
      showError('The resident does not owe a balance to be paid.');
    }
  };

  return (
    <PaymentModal 
      title="Please confirm your payment details." 
      onClose={onClose} 
      width={420}
      showCloseButton={!suppressOverlay}
    >
      <PaymentModalContent>
        {summaryRows.map(([label, value], index) => (
          <View key={index} style={styles.detailRow}>
            <Text style={styles.detailLabel}>{label}</Text>
            <Text style={styles.detailValue}>{value}</Text>
          </View>
        ))}

        <View style={styles.sectionDivider} />

        <View style={styles.footerRow}>
          <RNButton title="Cancel" variant="outlined" onPress={onClose} />
          <RNButton title="Make Payment" variant="primary" onPress={onConfirm} textStyle={styles.halfButtonText} />
        </View>
      </PaymentModalContent>
    </PaymentModal>
  );
};

const styles = StyleSheet.create({
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  detailLabel: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
  },
  detailValue: {
    fontSize: 16,
    fontWeight: '700',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.08)',
    marginTop: 8,
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  cancelBtn: {
    borderColor: RESIDENTS_DESKTOP_COLORS.BUTTON_SECONDARY,
  },
  cancelBtnText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  halfButtonText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
});

export default ConfirmPaymentDetailsModal;


